/**
 * Página de distribuições da unidade
 * 
 * Responsável por:
 * - Buscar dados das distribuições da unidade
 * - Exibir saldo da unidade
 * - Renderizar componentes de UI
 * 
 * @module pages/UnityDistributions
 */
import { Main } from "@/components/Main";
import { useEffect, useState } from "react";
import { getUnityBalances } from "@/Api/distributions";
import { CreateLoaderTable } from "@/components/Loaders";
import { TableData } from "@/App/UnityDistributions/Table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * Tipo dos dados de distribuição
 */
export type DistributionDataProps = {
  id: number;
  uuid: string;
  quota_quantity: number;
  contest_id: number;
  lottery_id: number;
  unity_id: number;
  cp_unity_balances_id: number;
  cp_classification_id: number;
  created_by: {
    id: number;
    name: string;
  };
  distribution_process_id: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  sellers: Array<{
    seller_id: number;
    quota_quantity: number;
  }>;
  contest: {
    id: number;
    uuid: string;
    name: string;
    validation_date: string;
    lottery_id: number;
    prize: number;
    is_active: boolean;
    tenant_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
  };
  cp_unity_balance: {
    id: number;
    unity_balance: number;
  };
  contest_product_classification: {
    id: number;
    product_name: string;
    product_classification_id: number;
    product_classification: {
      id: number;
      uuid: string;
      lottery_id: number;
      name: string;
      game_quantity: number;
      dez_quantity: string;
      quota_quantity: number;
      total_without_tariff: number;
      total_with_tariff: number;
      game_percentage: number;
      tariff: number;
      sale_price: number;
      price_without_tariff: number;
      commission_by_quote: number;
      image: string | null;
      is_active: number;
      product_type_id: number;
      tenant_id: number;
      created_at: string;
      updated_at: string;
      deleted_at: string | null;
      lottery: {
        id: number;
        name: string;
        color: string;
      };
    };
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  unity: {
    id: number;
    name: string;
    seller_received_without_distribution: boolean;
    has_stockist: boolean;
    is_active: boolean;
  };
  tenant: {
    id: number;
    name: string;
  };
  seller_distributions: Array<{
    id: number;
    contest_id: number;
    cp_classification_id: number;
    unity_id: number;
    seller_id: number;
    quota_quantity: number;
  }>;
};

/**
 * Componente de listagem de distribuições da unidade
 * 
 * @returns {JSX.Element} Componente renderizado
 */
export const PageList = () => {
  const [distributionsData, setDistributionsData] = useState<DistributionDataProps[]>([]);
  const [state, setState] = useState("loading");
  const [unityBalance, setUnityBalance] = useState<number | null>(null);
  const [unityInfo, setUnityInfo] = useState<any>(null);

  // Pega o ID da unidade do usuário logado (você pode adaptar conforme sua implementação)
  const getUnityId = () => {
    // Aqui você deve implementar a lógica para pegar o unity_id do usuário logado
    // Por exemplo, do localStorage, contexto, etc.
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    return userData?.unity_id || '2'; // Valor padrão para teste
  };

  // Função para carregar os dados
  const fetchData = async () => {
    try {
      setState("loading");
      const unityId = getUnityId();
      const { data } = await getUnityBalances(unityId);
      
      if (data?.response && data.response.length > 0) {
        setDistributionsData(data.response);
        
        // Pega o saldo da unidade do primeiro item (todos devem ter o mesmo saldo)
        setUnityBalance(data.response[0].cp_unity_balance.unity_balance);
        setUnityInfo(data.response[0].unity);
      } else {
        setDistributionsData([]);
        setUnityBalance(0);
        setUnityInfo(null);
      }
      
      setState("production");
    } catch (error) {
      console.error('Erro ao carregar distribuições:', error);
      setState("production");
    }
  };

  // Efeito para carregar dados iniciais
  useEffect(() => {
    fetchData();
  }, []);

  const canTakeBalance = unityInfo?.seller_received_without_distribution === true;

  return (
    <Main>
      <div className="flex justify-center md:justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-semibold flex gap-2 items-center">
          Distribuições da Unidade
        </h2>
      </div>

      {/* Card com informações da unidade */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unidade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unityInfo?.name || 'N/A'}</div>
            <p className="text-xs text-muted-foreground">
              {unityInfo?.is_active ? 'Ativa' : 'Inativa'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Saldo da Unidade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {unityBalance !== null ? unityBalance : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              Cotas disponíveis
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status de Retirada</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${canTakeBalance ? 'text-green-600' : 'text-red-600'}`}>
              {canTakeBalance ? 'Liberado' : 'Bloqueado'}
            </div>
            <p className="text-xs text-muted-foreground">
              {canTakeBalance ? 'Pode retirar sem distribuição' : 'Precisa de distribuição'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-3">
        {state === "loading" ? (
          <CreateLoaderTable cols={6} lines={6} />
        ) : (
          <TableData
            dataApi={distributionsData}
            canTakeBalance={canTakeBalance}
            onWithdrawSuccess={fetchData}
          />
        )}
      </div>
    </Main>
  );
}; 